import { useId, useState } from 'react';
import styled from 'styled-components';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { ArdoqId, AssetFolder, AssetType } from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { colors, space } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Field, FormSize, TextInputStyle } from '@ardoq/forms';
import { Icon, IconName } from '@ardoq/icons';
import { Popover2025 } from '@ardoq/popovers';
import {
  DropdownContainer,
  DropdownMaxHeight,
  DropdownMaxWidth,
  useDropdownTriggerContainer,
} from '@ardoq/dropdown-2026';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { assets$ } from 'streams/assets/assets$';
import { moveAssets } from 'streams/assetFolders/actions';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { combineFolders } from '../EntityBrowser/utils';
import { type Row } from '../EntityBrowser/types';
import { type AssetRow } from '../AssetsBrowser/types';
import { FolderSelect } from '../FolderSelectDialog/FolderSelect';
import { type SelectedFolder } from '../FolderSelectDialog/types';

export type LocationSelectProps = {
  /** Off where the surrounding dialog is itself a create-folder form. */
  canCreateFolder?: boolean;
  /** Match the surrounding form; `Field` otherwise stretches to its container. */
  formSize?: FormSize;
  folderId: Maybe<ArdoqId>;
  onChange: (folderId: ArdoqId | null) => void;
};

type LocationSelectStreamProps = {
  folders: AssetFolder[];
  dataSource: Row<AssetRow>[];
  i18nLocale: I18nLocale;
  locale: Locale;
};

/** A button, because it opens a popover, styled as the form field it sits among. */
const TriggerButton = styled(TextInputStyle)`
  cursor: pointer;
  text-align: left;

  /* A button matches :read-only, which TextInputStyle greys out. */
  &&:read-only {
    background-color: ${colors.surfaceDefault};
  }
`;

/**
 * FolderSelect sizes its table as a percentage once past seven rows, so the
 * dropdown has to give it a height rather than grow to fit.
 */
const TreeBox = styled.div`
  width: 400px;
  height: 372px;
  padding: ${space.small};
`;

const toSelectedFolder = (
  folderId: Maybe<ArdoqId>,
  folders: AssetFolder[]
): SelectedFolder => {
  const folder = folders.find(({ _id }) => _id === folderId);
  return folder ? { _id: folder._id, name: folder.name } : null;
};

const LocationSelectView = ({
  canCreateFolder,
  formSize,
  folderId,
  onChange,
  folders,
  dataSource,
  i18nLocale,
  locale,
}: LocationSelectProps & LocationSelectStreamProps) => {
  const selectedFolder = toSelectedFolder(folderId, folders);
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = useId();
  const triggerId = useId();
  const labelId = useId();
  const triggerRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen,
    openDropdown: () => setIsOpen(true),
  });

  const label = t(i18nLocale, 'locationSelect:label', 'Location');

  // Popover2025 does not return focus to its anchor, so closing has to (WCAG
  // 2.4.3) — but not when the user has already clicked something else.
  const close = () => {
    const active = document.activeElement;
    const hasFocusMovedAway =
      active !== null &&
      active !== document.body &&
      active !== triggerRef.current &&
      !document.getElementById(popoverId)?.contains(active);
    if (!hasFocusMovedAway) {
      triggerRef.current?.focus({ preventScroll: true });
    }
    setIsOpen(false);
  };

  return (
    <Field
      label={label}
      labelId={labelId}
      inputId={triggerId}
      formSize={formSize}
    >
      <TriggerButton
        as="button"
        type="button"
        id={triggerId}
        ref={triggerRef}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={popoverId}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <FlexBox
          gap="small"
          align="center"
          width="full"
          justify="space-between"
        >
          <FlexBox gap="small" align="center">
            {selectedFolder && <Icon iconName={IconName.FOLDER} />}
            <span>
              {selectedFolder?.name ??
                t(i18nLocale, 'locationSelect:noFolder', 'No folder')}
            </span>
          </FlexBox>
          <Icon
            iconName={isOpen ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN}
          />
        </FlexBox>
      </TriggerButton>
      <Popover2025
        id={popoverId}
        role="dialog"
        aria-labelledby={labelId}
        isOpen={isOpen}
        anchorRef={triggerRef}
        focusContent
        onClose={close}
        onClickOutside={close}
        placement={{ side: 'bottom', overflow: 'right', align: 'anchorEdge' }}
      >
        <DropdownContainer
          dropdownMaxWidth={DropdownMaxWidth.NONE}
          dropdownMaxHeight={DropdownMaxHeight.AUTO}
        >
          <TreeBox>
            <FolderSelect
              canCreateFolder={canCreateFolder}
              dataSource={dataSource}
              selectedFolder={selectedFolder}
              setSelectedFolder={folder => {
                // The table echoes its controlled selection back on mount, so
                // treating every emission as a pick would close the dropdown
                // immediately after opening it.
                if ((folder?._id ?? null) === (selectedFolder?._id ?? null)) {
                  return;
                }
                onChange(folder?._id ?? null);
                close();
              }}
              i18nLocale={i18nLocale}
              locale={locale}
            />
          </TreeBox>
        </DropdownContainer>
      </Popover2025>
    </Field>
  );
};

const toLocationSelectStreamProps = (
  folders: AssetFolder[],
  i18nLocale: I18nLocale,
  locale: Locale
): LocationSelectStreamProps => ({
  folders,
  dataSource: combineFolders(folders, []),
  i18nLocale,
  locale,
});

const folders$ = assets$.pipe(
  map(({ assetFoldersById }) => assetFoldersById),
  distinctUntilChanged(),
  map(assetFoldersById => Object.values(assetFoldersById))
);

export const LocationSelect = connect(
  LocationSelectView,
  combineLatest([folders$, i18nLocale$, locale$]).pipe(
    map(([folders, i18nLocale, locale]) =>
      toLocationSelectStreamProps(folders, i18nLocale, locale)
    )
  )
);

type AssetLocationSelectProps = {
  assetId: ArdoqId;
  assetType: AssetType;
  formSize?: FormSize;
};

type AssetLocationSelectStreamProps = {
  assetIdToFolderIdLookup: Record<ArdoqId, ArdoqId>;
};

const AssetLocationSelectView = ({
  assetId,
  assetType,
  formSize,
  assetIdToFolderIdLookup,
}: AssetLocationSelectProps & AssetLocationSelectStreamProps) => (
  <LocationSelect
    formSize={formSize}
    folderId={assetIdToFolderIdLookup[assetId] ?? null}
    onChange={folderId =>
      dispatchAction(
        moveAssets({
          folderId,
          movedAssets: [{ _id: assetId, type: assetType }],
        })
      )
    }
  />
);

/**
 * Folder membership lives on the folder, not on the asset, so an asset that
 * already exists is moved rather than saved into place.
 */
export const AssetLocationSelect = connect(
  AssetLocationSelectView,
  assets$.pipe(
    map(({ assetIdToFolderIdLookup }) => ({ assetIdToFolderIdLookup })),
    distinctUntilChanged(
      (a, b) => a.assetIdToFolderIdLookup === b.assetIdToFolderIdLookup
    )
  )
);
